/**
 * HR · Labour cost of work.
 *
 * Where the manpower cost meets the rest of the platform: every work order
 * with labour hours, priced at the hourly cost of the person who did it — or,
 * where the work order names nobody, at the mean rate of the trade that works
 * on its asset — rolled up by month, trade, asset type and asset; and the
 * preventive plans of Asset Management priced for a year.
 *
 * What cannot be priced is counted and said, never filled in: a job with no
 * hours, or with neither a doer nor an asset, stays out of the money.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useHrLabour } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Card, EmptyState, Segmented, Skeleton, Table } from "../../design/components";
import type { HrLabour } from "../../domain/hr";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useHrAccess } from "./access";
import { PreviewBar } from "./kit";
import {
  ColumnChart,
  Coverage,
  HrError,
  money,
  rate,
  RulesBasis,
  s,
  SampleBanner,
  sar,
  SUITE,
  Tiles,
  tradeLabel,
} from "./shared";

type Tab = "months" | "trades" | "types" | "assets" | "jobs" | "plans";
const TABS: { value: Tab; label: string }[] = [
  { value: "months", label: "By month" },
  { value: "trades", label: "By trade" },
  { value: "types", label: "By asset type" },
  { value: "assets", label: "Assets" },
  { value: "jobs", label: "Work orders" },
  { value: "plans", label: "Maintenance plans" },
];

export function HrLabourPage() {
  const session = useSession();
  const q = useHrLabour();
  const [tab, setTab] = useState<Tab>("months");
  const access = useHrAccess();
  const l = q.data;
  const priced = l ? l.coverage.byPerson + l.coverage.byTrade : 0;

  useScreenContext(
    l
      ? {
          route: "/hr-labour",
          title: "Labour cost of work",
          summary: [
            t("Labour on the work of the last {m} months: {h} hours, {c}.", {
              m: l.months,
              h: l.totals.hours.toLocaleString("en"),
              c: money(l.totals.cost),
            }),
            t("{p} of {w} work orders with hours are priced — {a} at the doer's own rate, {b} at their trade's.", {
              p: priced,
              w: l.coverage.withHours,
              a: l.coverage.byPerson,
              b: l.coverage.byTrade,
            }),
            t("The preventive plans need {h} hours a year, {c} of labour.", {
              h: l.plans.annualHours.toLocaleString("en"),
              c: money(l.plans.annualCost),
            }),
          ],
          offers: [
            { label: t("Which assets cost most in labour?"), ask: "which assets have the highest labour cost?" },
            {
              label: t("What does preventive maintenance cost in labour?"),
              ask: "what is the annual labour cost of the maintenance plans?",
            },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Labour cost of work">
      <PreviewBar />
      <SampleBanner />
      {!access.can.pay && <HrError error={Object.assign(new Error("Not permitted"), { status: 403 })} />}
      <p className={s.note}>
        {t(
          "Each person's monthly cost is spread over their working hours to get an hourly rate for jobs.",
        )}
      </p>
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {l && access.can.pay && (
        <>
          <Tiles
            items={[
              {
                key: "cost",
                label: t("Labour on work, {m} months", { m: l.months }),
                value: money(l.totals.cost),
                sub: t("{h} hours priced", { h: l.totals.hours.toLocaleString("en") }),
              },
              {
                key: "priced",
                label: t("Work orders priced"),
                value: `${priced} / ${l.coverage.withHours}`,
                sub: t("{a} by the doer, {b} by the trade", { a: l.coverage.byPerson, b: l.coverage.byTrade }),
                tone: l.coverage.withHours && priced < l.coverage.withHours ? "warn" : undefined,
              },
              {
                key: "nohours",
                label: t("Closed without hours"),
                value: l.coverage.noHours,
                tone: l.coverage.noHours ? "warn" : "good",
                sub: t("of {n} work orders", { n: l.coverage.workOrders }),
              },
              {
                key: "plans",
                label: t("Preventive plans, a year"),
                value: money(l.plans.annualCost),
                sub: t("{h} hours of labour", { h: l.plans.annualHours.toLocaleString("en") }),
              },
            ]}
          />
          {l.coverage.workOrders > 0 && (
            <Coverage
              lines={[
                {
                  label: t("of the work orders record labour hours"),
                  of: l.coverage.workOrders,
                  n: l.coverage.withHours,
                },
                {
                  label: t("of those are priced at the doer's own rate"),
                  of: l.coverage.withHours,
                  n: l.coverage.byPerson,
                },
                { label: t("of those are priced at a trade's rate"), of: l.coverage.withHours, n: l.coverage.byTrade },
              ]}
            />
          )}
          {l.coverage.withHours > 0 && l.coverage.byPerson === 0 && (
            <p className={s.note}>
              {t(
                "No work order uses a person's own rate yet. Link each technician to their user on the Workforce screen, and have them close their own work orders.",
              )}{" "}
              <Link to="/hr-people">{t("Workforce")}</Link>
            </p>
          )}
          <Segmented value={tab} options={TABS} onChange={setTab} ariaLabel="Labour views" />
          {tab === "months" && <Months l={l} />}
          {tab === "trades" && <Trades l={l} />}
          {tab === "types" && <Types l={l} />}
          {tab === "assets" && <Assets l={l} />}
          {tab === "jobs" && <Jobs l={l} />}
          {tab === "plans" && <Plans l={l} />}
          <p className={s.note}>
            {t("How it is worked out:")} {t(l.method)}
          </p>
          <RulesBasis basis={l.rulesBasis ?? "default"} />
        </>
      )}
    </Page>
  );
}

function Months({ l }: { l: HrLabour }) {
  return (
    <Card title={t("Labour cost by month")} sub={t("Priced work orders, by month completed.")}>
      {l.totals.cost === 0 ? (
        <EmptyState>{t("No work order in the period could be priced.")}</EmptyState>
      ) : (
        <ColumnChart
          label={t("Labour cost by month")}
          format={(v) => money(v).replace("SAR ", "")}
          data={l.byMonth.map((m) => ({ key: m.month, label: m.month.slice(2), value: m.cost }))}
        />
      )}
    </Card>
  );
}

function Trades({ l }: { l: HrLabour }) {
  return (
    <div className={s.split}>
      <Card title={t("Labour by trade")}>
        {l.byTrade.length === 0 ? (
          <EmptyState>{t("Nothing priced yet.")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Trade")}</th>
                <th className={s.num}>{t("Work orders")}</th>
                <th className={s.num}>{t("Hours")}</th>
                <th className={s.num}>{t("Cost")}</th>
              </tr>
            </thead>
            <tbody>
              {l.byTrade.map((r) => (
                <tr key={r.key}>
                  <td>{tradeLabel(r.key)}</td>
                  <td className={s.num}>{r.workOrders}</td>
                  <td className={s.num}>{r.hours}</td>
                  <td className={s.num}>{sar(r.cost)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card
        title={t("What an hour of each trade costs")}
        sub={t("Average for the trade, from Manpower cost.")}
      >
        {l.tradeRates.length === 0 ? (
          <EmptyState>{t("No trade has a costed person yet.")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Trade")}</th>
                <th className={s.num}>{t("People")}</th>
                <th className={s.num}>{t("An hour")}</th>
              </tr>
            </thead>
            <tbody>
              {l.tradeRates.map((r) => (
                <tr key={r.trade}>
                  <td>{tradeLabel(r.trade)}</td>
                  <td className={s.num}>{r.heads}</td>
                  <td className={s.num}>{rate(r.rate)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Types({ l }: { l: HrLabour }) {
  return (
    <Card title={t("Labour by asset type")} sub={t("Which asset types take the most staff time.")}>
      {l.byAssetType.length === 0 ? (
        <EmptyState>{t("Nothing priced yet.")}</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Asset type")}</th>
              <th className={s.num}>{t("Work orders")}</th>
              <th className={s.num}>{t("Hours")}</th>
              <th className={s.num}>{t("Cost")}</th>
            </tr>
          </thead>
          <tbody>
            {l.byAssetType.map((r) => (
              <tr key={r.key}>
                <td>{r.key === "—" ? t("No asset named") : r.name}</td>
                <td className={s.num}>{r.workOrders}</td>
                <td className={s.num}>{r.hours}</td>
                <td className={s.num}>{sar(r.cost)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function Assets({ l }: { l: HrLabour }) {
  return (
    <Card
      title={t("The assets that cost most in labour")}
      sub={t("Open one to see it in Asset Management.")}
    >
      {l.topAssets.length === 0 ? (
        <EmptyState>{t("No priced work order names an asset yet.")}</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Tag")}</th>
              <th>{t("Type")}</th>
              <th className={s.num}>{t("Work orders")}</th>
              <th className={s.num}>{t("Hours")}</th>
              <th className={s.num}>{t("Labour cost")}</th>
            </tr>
          </thead>
          <tbody>
            {l.topAssets.map((a) => (
              <tr key={a.assetId}>
                <td className={s.mono}>
                  <Link to={`/asset-register/${encodeURIComponent(a.assetId)}`}>{a.tag ?? a.assetId}</Link>
                </td>
                <td>{a.typeName ?? "—"}</td>
                <td className={s.num}>{a.workOrders}</td>
                <td className={s.num}>{a.hours}</td>
                <td className={s.num}>{sar(a.cost)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function Jobs({ l }: { l: HrLabour }) {
  return (
    <Card
      title={t("Work orders with labour hours")}
      sub={t("Latest 200, with each rate and its source.")}
    >
      {l.workOrders.length === 0 ? (
        <EmptyState>{t("No work order in the period records labour hours.")}</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Number")}</th>
              <th>{t("Title")}</th>
              <th>{t("Asset")}</th>
              <th>{t("Done by")}</th>
              <th className={s.num}>{t("Hours")}</th>
              <th className={s.num}>{t("Rate")}</th>
              <th>{t("Priced by")}</th>
              <th className={s.num}>{t("Cost")}</th>
            </tr>
          </thead>
          <tbody>
            {l.workOrders.map((w) => (
              <tr key={w.id}>
                <td className={s.mono}>{w.number}</td>
                <td>{w.title}</td>
                <td className={s.mono}>
                  {w.assetId ? (
                    <Link to={`/asset-register/${encodeURIComponent(w.assetId)}`}>{w.assetTag ?? "—"}</Link>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{w.person ?? <span className={s.dim}>{t("not recorded")}</span>}</td>
                <td className={s.num}>{w.hours}</td>
                <td className={s.num}>{rate(w.rate)}</td>
                <td>
                  {w.rateSource === "person" ? (
                    t("the doer")
                  ) : w.rateSource === "trade" ? (
                    t("{trade} trade", { trade: tradeLabel(w.trade) })
                  ) : (
                    <span className={s.dim}>{t("cannot be priced")}</span>
                  )}
                </td>
                <td className={s.num}>{sar(w.cost)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function Plans({ l }: { l: HrLabour }) {
  return (
    <Card
      title={t("Preventive plans, a year of labour")}
      sub={t("Estimated hours × times due a year × trade hourly cost.")}
      actions={<Link to="/asset-maintenance">{t("Maintenance plans")}</Link>}
    >
      {l.plans.rows.length === 0 ? (
        <EmptyState>{t("No active maintenance plans. Create them in Asset Management.")}</EmptyState>
      ) : (
        <>
          {l.plans.uncosted > 0 && (
            <p className={s.note}>
              {t("{n} plans can't be priced: missing hours, interval, or a costed person in the trade.", {
                n: l.plans.uncosted,
              })}
            </p>
          )}
          <Table>
            <thead>
              <tr>
                <th>{t("Plan")}</th>
                <th>{t("Asset")}</th>
                <th>{t("Trade")}</th>
                <th className={s.num}>{t("Hours each")}</th>
                <th className={s.num}>{t("Times a year")}</th>
                <th className={s.num}>{t("Hours a year")}</th>
                <th className={s.num}>{t("Rate")}</th>
                <th className={s.num}>{t("A year")}</th>
              </tr>
            </thead>
            <tbody>
              {l.plans.rows.map((p) => (
                <tr key={p.id}>
                  <td>{p.title}</td>
                  <td className={s.mono}>
                    {p.assetId ? (
                      <Link to={`/asset-register/${encodeURIComponent(p.assetId)}`}>{p.assetTag ?? "—"}</Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{tradeLabel(p.trade)}</td>
                  <td className={s.num}>{p.estimatedHours ?? "—"}</td>
                  <td className={s.num}>{p.perYear ?? "—"}</td>
                  <td className={s.num}>{p.hoursPerYear ?? "—"}</td>
                  <td className={s.num}>{rate(p.rate)}</td>
                  <td className={s.num}>{sar(p.costPerYear)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Card>
  );
}
